import { useIntl } from 'react-intl'
import { styled, alpha } from '@mui/material/styles'
import { IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/EditRounded'
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import { colorPalette } from '../../style/colorPalette'
import { AiAgentQueuedMessage } from './queuedMessages'

// 送信待ちは入力欄のすぐ上に居座らせる。高さを取るぶんメッセージ一覧側が縮むが、
// これから送る内容の方が手元の関心事なので、そちらを優先して見せる。
// 溜まるのはせいぜい数件なので件数で高さを打ち切らず、パネルが狭いときだけスクロールさせる。
const ListRoot = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: 12,
  overflowY: 'auto',
})

const MessageCard = styled('div', {
  shouldForwardProp: prop => prop !== 'isEditing',
})<{ isEditing: boolean }>(({ isEditing }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minWidth: 0,
  padding: '8px 12px',
  borderRadius: 8,
  border: `1px solid ${
    isEditing ? colorPalette.monotone[2] : colorPalette.skyBlue[2]
  }`,
  backgroundColor: isEditing
    ? colorPalette.monotone[1]
    : colorPalette.skyBlue[1],
  boxShadow: `0 1px 3px ${alpha(colorPalette.monotone[10], 0.12)}`,
  fontSize: 13,
  lineHeight: 1.4,
}))

const CardActions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  flexShrink: 0,
})

const ActionButton = styled(IconButton)({
  width: 22,
  height: 22,
  padding: 0,
  color: colorPalette.monotone[5],
  '&:hover': {
    color: colorPalette.skyBlue[8],
    backgroundColor: alpha(colorPalette.skyBlue[7], 0.12),
  },
  '&.Mui-disabled': {
    color: colorPalette.monotone[3],
  },
  '& .MuiSvgIcon-root': {
    width: 16,
    height: 16,
  },
})

// minWidth: 0 が無いと flex アイテムが本文の全長まで広がり、カードからはみ出す。
const CardText = styled('span', {
  shouldForwardProp: prop => prop !== 'muted',
})<{ muted: boolean }>(({ muted }) => ({
  flexGrow: 1,
  minWidth: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: muted ? colorPalette.monotone[5] : colorPalette.monotone[10],
}))

const EditingLabel = styled('span')({
  flexShrink: 0,
  color: colorPalette.monotone[5],
  fontSize: 11,
  fontWeight: 600,
})

// 何を添えて送るのかが本文だけでは分からないため、種類ごとに件数で知らせる。
const AttachmentBadges = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexShrink: 0,
  color: colorPalette.skyBlue[8],
  fontSize: 11,
  fontWeight: 600,
})

const AttachmentBadge = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
  '& .MuiSvgIcon-root': {
    width: 14,
    height: 14,
  },
})

// 添付の件数。種類ごとに何件添えて送るのかを知らせるために使う。
export type QueuedMessageAttachmentCounts = {
  images: number
  wbsItems: number
}

type QueuedMessageListProps = {
  messages: AiAgentQueuedMessage[]
  // 編集中の項目。行列には残したまま「編集中」として見せる。
  editingMessageId?: string
  // 編集中の項目の添付件数。編集中は添付が入力欄側にあり、項目側は確定するまで
  // 編集前のままなので、入力欄の件数を渡してもらって表示する。
  editingAttachmentCounts?: QueuedMessageAttachmentCounts
  // 添付のアップロード中など、入力欄を編集に使えない状態。
  disableEdit?: boolean
  onEdit: (id: string) => void
  onRemove: (id: string) => void
}

/**
 * AI応答中に送られ、応答完了後にまとめて送信されるメッセージの一覧。
 * 編集ボタンで入力欄へ戻して編集できる（編集中は他のカードを編集できない）。
 * 編集中に入力欄へ添付したものはその項目に付くため、件数は編集中のカードにも出す。
 */
export const QueuedMessageList = ({
  messages,
  editingMessageId,
  editingAttachmentCounts,
  disableEdit,
  onEdit,
  onRemove,
}: QueuedMessageListProps) => {
  const intl = useIntl()

  if (messages.length === 0) {
    return null
  }

  return (
    <ListRoot>
      {messages.map(message => {
        const isEditing = message.id === editingMessageId
        const attachmentCounts =
          isEditing && editingAttachmentCounts
            ? editingAttachmentCounts
            : {
                images: message.images.length,
                wbsItems: message.wbsItems.length,
              }
        return (
          <MessageCard key={message.id} isEditing={isEditing}>
            {isEditing ? (
              <EditingLabel>
                {intl.formatMessage({ id: 'aiAgentChat.queue.editing' })}
              </EditingLabel>
            ) : (
              <CardActions>
                <ActionButton
                  size="small"
                  onClick={() => onRemove(message.id)}
                  title={intl.formatMessage({
                    id: 'aiAgentChat.queue.remove',
                  })}
                  aria-label={intl.formatMessage({
                    id: 'aiAgentChat.queue.remove',
                  })}
                >
                  <CloseIcon fontSize="inherit" />
                </ActionButton>
                <ActionButton
                  size="small"
                  // 別の項目を編集している間は、入力欄がふさがっているため編集できない。
                  disabled={!!editingMessageId || !!disableEdit}
                  onClick={() => onEdit(message.id)}
                  title={intl.formatMessage({ id: 'aiAgentChat.queue.edit' })}
                  aria-label={intl.formatMessage({
                    id: 'aiAgentChat.queue.edit',
                  })}
                >
                  <EditIcon fontSize="inherit" />
                </ActionButton>
              </CardActions>
            )}
            <CardText muted={isEditing}>{message.text}</CardText>
            {(attachmentCounts.images > 0 || attachmentCounts.wbsItems > 0) && (
              <AttachmentBadges>
                {attachmentCounts.images > 0 && (
                  <AttachmentBadge
                    title={intl.formatMessage(
                      { id: 'aiAgentChat.queue.imageCount' },
                      { count: attachmentCounts.images }
                    )}
                  >
                    <AttachFileRoundedIcon fontSize="inherit" />
                    {attachmentCounts.images}
                  </AttachmentBadge>
                )}
                {attachmentCounts.wbsItems > 0 && (
                  <AttachmentBadge
                    title={intl.formatMessage(
                      { id: 'aiAgentChat.queue.wbsItemCount' },
                      { count: attachmentCounts.wbsItems }
                    )}
                  >
                    <AccountTreeOutlinedIcon fontSize="inherit" />
                    {attachmentCounts.wbsItems}
                  </AttachmentBadge>
                )}
              </AttachmentBadges>
            )}
          </MessageCard>
        )
      })}
    </ListRoot>
  )
}
